import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  TrendingUp, Users, Activity, Package, Clock, Download,
  ArrowLeft, Eye, ShoppingCart, Check, Tag, AlertCircle, DollarSign, CalendarRange, X as XIcon,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = 'var(--color-primary)';
const GOLD_LIGHT = 'var(--color-primary-hover)';
const BG = 'var(--color-surface)';

const TABS = [
  { id: 'traffic', label: 'Trafic', icon: Activity },
  { id: 'timeseries', label: 'Ventes', icon: TrendingUp },
  { id: 'funnel', label: 'Entonnoir', icon: ShoppingCart },
  { id: 'products', label: 'Produits', icon: Package },
  { id: 'customers', label: 'Clients', icon: Users },
  { id: 'profits', label: 'Bénéfices', icon: DollarSign },
  { id: 'operations', label: 'Opérations', icon: Clock },
];

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const fmtEuro = (n) => `${(n || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} €`;
const fmtInt = (n) => (n || 0).toLocaleString('fr-FR');
const fmtPct = (n) => `${(n || 0).toFixed(1)} %`;
const shortDate = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

// ---------- Small presentational primitives ----------

const KPI = ({ label, value, sub, icon: Icon, testid }) => (
  <div className="border border-white/10 bg-[color:var(--color-surface)] p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em]">{label}</p>
      {Icon && <Icon className="w-4 h-4 text-zinc-600" />}
    </div>
    <p className="text-white text-2xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
      {value}
    </p>
    {sub && <p className="text-zinc-500 text-xs mt-1">{sub}</p>}
  </div>
);

const Panel = ({ title, children, testid }) => (
  <div className="border border-white/10 bg-[color:var(--color-surface)] p-5" data-testid={testid}>
    {title && (
      <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-3">{title}</p>
    )}
    {children}
  </div>
);

const Empty = ({ text = 'Aucune donnée pour cette période' }) => (
  <p className="text-zinc-600 text-sm italic py-4">{text}</p>
);

// ---------- Page ----------

const AdminAnalytics = () => {
  const { hasPermission } = useAuth();
  const canAccess = hasPermission('analytics');
  const navigate = useNavigate();
  const [tab, setTab] = useState('traffic');
  const [days, setDays] = useState(30);
  const [endDate, setEndDate] = useState(null); // null = today (rolling window)
  const [presetLabel, setPresetLabel] = useState('30j');
  const [showCustom, setShowCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [profitScope, setProfitScope] = useState('realized');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canAccess) navigate('/connexion');
  }, [canAccess, navigate]);

  const fetchTab = useCallback(async (tabId, period, scope, end) => {
    setLoading(true);
    try {
      const endpointMap = {
        traffic: 'traffic',
        timeseries: 'timeseries',
        funnel: 'funnel',
        products: 'products-insights',
        customers: 'customers-insights',
        profits: 'profits',
        operations: 'operations',
      };
      let url = `${API_URL}/api/admin/analytics/${endpointMap[tabId]}?days=${period}`;
      if (end) url += `&end_date=${end}`;
      if (tabId === 'profits') url += `&scope=${scope}`;
      const res = await axios.get(url);
      setData((d) => ({ ...d, [tabId]: res.data }));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.debug('[analytics]', e);
      toast.error('Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTab(tab, days, profitScope, endDate);
  }, [tab, days, endDate, profitScope, fetchTab]);

  // ---- Period presets ----
  const isoDate = (d) => d.toISOString().slice(0, 10);

  const applyPreset = useCallback((key) => {
    const today = new Date();
    if (key === 'week') {
      setDays(7); setEndDate(null); setPresetLabel('Semaine');
    } else if (key === 'month') {
      setDays(30); setEndDate(null); setPresetLabel('Mois');
    } else if (key === '90') {
      setDays(90); setEndDate(null); setPresetLabel('3 mois');
    } else if (key === 'year') {
      setDays(365); setEndDate(null); setPresetLabel('1 an');
    } else if (key === 'prev_month') {
      // First → last day of previous month
      const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastPrev = new Date(firstThis.getTime() - 24 * 3600 * 1000);
      const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
      const diff = Math.round((lastPrev - firstPrev) / (24 * 3600 * 1000)) + 1;
      setDays(diff); setEndDate(isoDate(lastPrev));
      setPresetLabel(`Mois précédent (${lastPrev.toLocaleString('fr-FR', { month: 'long', year: 'numeric' })})`);
    } else if (key === 'prev_week') {
      // Previous ISO week: Mon → Sun before this week's Monday
      const day = today.getDay() || 7; // Sun=0 → 7
      const thisMon = new Date(today); thisMon.setDate(today.getDate() - day + 1);
      const prevSun = new Date(thisMon.getTime() - 24 * 3600 * 1000);
      setDays(7); setEndDate(isoDate(prevSun));
      setPresetLabel('Semaine précédente');
    }
  }, []);

  const applyCustom = () => {
    if (!customFrom || !customTo) { toast.error('Choisissez les deux dates'); return; }
    const from = new Date(customFrom);
    const to = new Date(customTo);
    if (from > to) { toast.error('La date de début doit précéder la date de fin'); return; }
    const diff = Math.round((to - from) / (24 * 3600 * 1000)) + 1;
    if (diff > 365) { toast.error('Plage max : 365 jours'); return; }
    setDays(diff); setEndDate(isoDate(to));
    setPresetLabel(`${customFrom} → ${customTo}`);
    setShowCustom(false);
  };

  const downloadCSV = async () => {
    const exportable = ['traffic', 'timeseries', 'funnel', 'products', 'customers', 'profits'];
    if (!exportable.includes(tab)) {
      toast.info("L'export CSV n'est pas disponible pour cette section");
      return;
    }
    try {
      let url = `${API_URL}/api/admin/analytics/export?section=${tab}&days=${days}`;
      if (endDate) url += `&end_date=${endDate}`;
      if (tab === 'profits') url += `&scope=${profitScope}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      const suffix = endDate ? `${endDate}-${days}j` : `${days}j`;
      link.setAttribute('download', `analytics-${tab}-${suffix}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Export CSV téléchargé');
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.debug('[analytics]', e);
      toast.error("Échec de l'export");
    }
  };

  if (!canAccess) return null;

  const current = data[tab];

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16" data-testid="admin-analytics-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div>
            <Link
              to="/admin"
              className="text-zinc-500 hover:text-[color:var(--color-primary)] text-xs flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-3 h-3" /> Tableau de bord
            </Link>
            <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">
              Analytics Pro
            </p>
            <h1
              className="text-3xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Statistiques avancées
            </h1>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2 flex-wrap" data-testid="period-presets">
              {[
                { k: 'week', label: 'Semaine' },
                { k: 'month', label: 'Mois' },
                { k: '90', label: '3 mois' },
                { k: 'year', label: '1 an' },
                { k: 'prev_week', label: 'Sem. -1' },
                { k: 'prev_month', label: 'Mois -1' },
              ].map((p) => {
                const active =
                  (p.k === 'week' && days === 7 && !endDate) ||
                  (p.k === 'month' && days === 30 && !endDate) ||
                  (p.k === '90' && days === 90 && !endDate) ||
                  (p.k === 'year' && days === 365 && !endDate) ||
                  (p.k === 'prev_week' && presetLabel === 'Semaine précédente') ||
                  (p.k === 'prev_month' && presetLabel.startsWith('Mois précédent'));
                return (
                  <button
                    key={p.k}
                    type="button"
                    onClick={() => applyPreset(p.k)}
                    className={`px-3 py-1.5 text-xs border transition-colors ${
                      active
                        ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-black'
                        : 'border-white/10 text-zinc-400 hover:border-white/30'
                    }`}
                    data-testid={`period-preset-${p.k}`}
                  >
                    {p.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setShowCustom(true)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs border transition-colors ${
                  presetLabel.includes('→')
                    ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-black'
                    : 'border-white/10 text-zinc-400 hover:border-white/30'
                }`}
                data-testid="period-custom-btn"
              >
                <CalendarRange className="w-3 h-3" />
                Personnalisé
              </button>
              <button
                type="button"
                onClick={downloadCSV}
                className="flex items-center gap-1 border border-[color:rgb(var(--primary-rgb)/0.30)] text-[color:var(--color-primary)] px-3 py-1.5 text-xs hover:bg-[color:var(--color-primary)] hover:text-black transition-colors"
                data-testid="analytics-export-csv"
              >
                <Download className="w-3 h-3" />
                CSV
              </button>
            </div>
            <p className="text-zinc-500 text-[11px]" data-testid="period-label">
              Période active : <span className="text-[color:var(--color-primary)]">{presetLabel}</span>
              {endDate && <> · jusqu'au <span className="text-zinc-300">{endDate}</span></>}
            </p>
          </div>
        </div>

        {/* Custom date range modal */}
        {showCustom && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
               data-testid="custom-period-modal">
            <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl text-white font-light"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Plage personnalisée
                </h3>
                <button type="button" onClick={() => setShowCustom(false)}
                  className="text-zinc-500 hover:text-white">
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="text-zinc-500 text-xs mb-4">
                Choisissez une période exacte (max 365 jours).
              </p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Du</label>
                  <input type="date" value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    data-testid="custom-from"
                    className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
                </div>
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Au</label>
                  <input type="date" value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    data-testid="custom-to"
                    className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={applyCustom}
                  data-testid="custom-apply"
                  className="flex-1 bg-[color:var(--color-primary)] text-black py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)]">
                  Appliquer
                </button>
                <button type="button" onClick={() => setShowCustom(false)}
                  className="border border-white/20 text-zinc-400 px-5 py-2.5 text-xs uppercase tracking-wider hover:border-white/40">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-8 overflow-x-auto border-b border-white/10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] whitespace-nowrap border-b-2 transition-colors ${
                  active
                    ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)]'
                    : 'border-transparent text-zinc-500 hover:text-white'
                }`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-[color:var(--color-primary)] text-sm">Chargement…</div>
          </div>
        )}

        {!loading && tab === 'traffic' && current && <TrafficTab d={current} />}
        {!loading && tab === 'timeseries' && current && <TimeseriesTab d={current} />}
        {!loading && tab === 'funnel' && current && <FunnelTab d={current} />}
        {!loading && tab === 'products' && current && <ProductsTab d={current} />}
        {!loading && tab === 'customers' && current && <CustomersTab d={current} />}
        {!loading && tab === 'profits' && current && (
          <ProfitsTab
            d={current}
            scope={profitScope}
            onScopeChange={setProfitScope}
          />
        )}
        {!loading && tab === 'operations' && current && <OperationsTab d={current} />}
      </div>
    </div>
  );
};

// ========== Tabs ==========

const TrafficTab = ({ d }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <KPI label="Pages vues" value={fmtInt(d.total_views)} icon={Eye} testid="kpi-views" />
      <KPI label="Visiteurs uniques" value={fmtInt(d.unique_visitors)} icon={Users} testid="kpi-visitors" />
      <KPI label="Pages / visiteur" value={d.avg_pages_per_visitor?.toFixed(1) || '0'} testid="kpi-pagespervisitor" />
      <KPI label="Heure de pointe" value={`${String(d.peak_hour).padStart(2, '0')}:00`} icon={Clock} testid="kpi-peakhour" />
    </div>

    <Panel title="Trafic quotidien" testid="chart-daily-traffic">
      {d.daily?.length ? (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={d.daily}>
            <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={shortDate} stroke="var(--color-text-muted)" tick={{ fontSize: 11 }} />
            <YAxis stroke="var(--color-text-muted)" tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ background: BG, border: '1px solid var(--color-border)' }} labelFormatter={shortDate} />
            <Line type="monotone" dataKey="views" stroke={GOLD} strokeWidth={2} dot={false} name="Pages vues" />
            <Line type="monotone" dataKey="visitors" stroke="#7dd3fc" strokeWidth={2} dot={false} name="Visiteurs uniques" />
          </LineChart>
        </ResponsiveContainer>
      ) : <Empty />}
    </Panel>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Panel title="Répartition par appareil" testid="panel-devices">
        <DeviceBars items={d.devices} />
      </Panel>
      <Panel title="Top langues" testid="panel-languages">
        <DeviceBars items={d.languages} />
      </Panel>
    </div>

    <Panel title="Répartition horaire" testid="panel-hourly">
      <ResponsiveContainer width="100%" height={150}>
        <BarChart data={(d.hourly || []).map((v, h) => ({ h: `${h}h`, v }))}>
          <XAxis dataKey="h" stroke="var(--color-text-muted)" tick={{ fontSize: 10 }} />
          <YAxis stroke="var(--color-text-muted)" tick={{ fontSize: 10 }} />
          <Tooltip contentStyle={{ background: BG, border: '1px solid var(--color-border)' }} />
          <Bar dataKey="v" fill={GOLD} name="Vues" />
        </BarChart>
      </ResponsiveContainer>
    </Panel>

    <Panel title="Top pages visitées" testid="panel-top-pages">
      {d.top_pages?.length ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
              <th className="text-left py-2">Chemin</th>
              <th className="text-right py-2">Vues</th>
              <th className="text-right py-2">Visiteurs</th>
            </tr>
          </thead>
          <tbody>
            {d.top_pages.map((p) => (
              <tr key={p.path} className="border-b border-white/5">
                <td className="py-2 text-white font-mono text-xs">{p.path}</td>
                <td className="py-2 text-right text-white">{fmtInt(p.views)}</td>
                <td className="py-2 text-right text-zinc-400">{fmtInt(p.visitors)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty />}
    </Panel>
  </div>
);

const DeviceBars = ({ items }) => {
  const entries = Object.entries(items || {});
  if (!entries.length) return <Empty />;
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-zinc-400 capitalize">{k}</span>
            <span className="text-white">{fmtInt(v)} ({((v / total) * 100).toFixed(1)} %)</span>
          </div>
          <div className="h-1.5 bg-white/5">
            <div
              className="h-full bg-[color:var(--color-primary)]"
              style={{ width: `${(v / total) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const TimeseriesTab = ({ d }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <KPI label="Revenu livré" value={fmtEuro(d.totals?.revenue)} icon={TrendingUp} testid="kpi-revenue" />
      <KPI label="Commandes" value={fmtInt(d.totals?.orders)} icon={ShoppingCart} testid="kpi-orders" />
      <KPI label="Panier moyen" value={fmtEuro(d.totals?.avg_order_value)} testid="kpi-aov" />
      <KPI label="Nouvelles inscriptions" value={fmtInt(d.totals?.signups)} icon={Users} testid="kpi-signups" />
    </div>

    <Panel title="Revenu livré par jour (€)" testid="chart-revenue">
      {d.daily_revenue?.length ? (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={d.daily_revenue}>
            <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={shortDate} stroke="var(--color-text-muted)" tick={{ fontSize: 11 }} />
            <YAxis stroke="var(--color-text-muted)" tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{ background: BG, border: '1px solid var(--color-border)' }}
              labelFormatter={shortDate}
              formatter={(v, name) => name === 'revenue' ? fmtEuro(v) : v}
            />
            <Bar dataKey="revenue" fill={GOLD} name="Revenu" />
          </BarChart>
        </ResponsiveContainer>
      ) : <Empty />}
    </Panel>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Panel title="Commandes par jour" testid="chart-orders">
        {d.daily_revenue?.length ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={d.daily_revenue}>
              <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={shortDate} stroke="var(--color-text-muted)" tick={{ fontSize: 10 }} />
              <YAxis stroke="var(--color-text-muted)" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: BG, border: '1px solid var(--color-border)' }} labelFormatter={shortDate} />
              <Bar dataKey="orders" fill={GOLD_LIGHT} name="Commandes" />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Panel>

      <Panel title="Nouvelles inscriptions par jour" testid="chart-signups">
        {d.daily_signups?.length ? (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={d.daily_signups}>
              <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={shortDate} stroke="var(--color-text-muted)" tick={{ fontSize: 10 }} />
              <YAxis stroke="var(--color-text-muted)" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: BG, border: '1px solid var(--color-border)' }} labelFormatter={shortDate} />
              <Line type="monotone" dataKey="count" stroke="#7dd3fc" strokeWidth={2} dot={false} name="Inscriptions" />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Panel>
    </div>
  </div>
);

const FunnelTab = ({ d }) => {
  const max = Math.max(...(d.steps || []).map((s) => s.count), 1);
  return (
    <div className="space-y-6">
      <Panel title={`Entonnoir de conversion — ${d.period_days} derniers jours`} testid="panel-funnel">
        {d.steps?.length ? (
          <div className="space-y-3">
            {d.steps.map((s, i) => {
              const w = Math.max((s.count / max) * 100, 4);
              return (
                <div key={s.name} data-testid={`funnel-step-${i}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-white">{s.name}</span>
                    <span className="text-zinc-400">
                      {fmtInt(s.count)}
                      {i > 0 && (
                        <span className="text-[color:var(--color-primary)] ml-3">
                          {fmtPct(s.rate_from_previous)} vs étape précédente
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="h-7 bg-white/5 relative overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[color:var(--color-primary)] to-[color:var(--color-primary-hover)] flex items-center justify-end pr-2"
                      style={{ width: `${w}%` }}
                    >
                      <span className="text-black text-xs font-medium">{fmtPct(s.rate_from_top)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : <Empty />}
      </Panel>
      <p className="text-zinc-500 text-xs italic">
        Le taux « vs étape précédente » peut dépasser 100 % : une même session peut consulter plusieurs produits, et certaines commandes proviennent de sessions antérieures à la période.
      </p>
    </div>
  );
};

const ProductsTab = ({ d }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <KPI label="Valeur du stock" value={fmtEuro(d.total_stock_value)} icon={Package} testid="kpi-stock-value" />
      <KPI label="Produits dormants" value={fmtInt(d.dormant_count)} sub="Jamais commandés, en stock" icon={AlertCircle} testid="kpi-dormant" />
      <KPI label="Catégories actives" value={fmtInt(d.top_categories?.length)} testid="kpi-cats" />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Panel title="Top catégories par revenu" testid="panel-top-categories">
        <RevenueList items={d.top_categories} />
      </Panel>
      <Panel title="Top marques par revenu" testid="panel-top-brands">
        <RevenueList items={d.top_brands} />
      </Panel>
    </div>

    <Panel title="Produits dormants — priorité destockage" testid="panel-dormant">
      {d.dormant_products?.length ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
              <th className="text-left py-2">Produit</th>
              <th className="text-left py-2">Marque</th>
              <th className="text-right py-2">Stock</th>
              <th className="text-right py-2">Valeur</th>
            </tr>
          </thead>
          <tbody>
            {d.dormant_products.map((p) => (
              <tr key={p.id} className="border-b border-white/5">
                <td className="py-2 text-white">{p.name}</td>
                <td className="py-2 text-zinc-400">{p.brand}</td>
                <td className="py-2 text-right text-white">{p.stock}</td>
                <td className="py-2 text-right text-[color:var(--color-primary)]">{fmtEuro(p.stock_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty text="Aucun produit dormant — bravo !" />}
    </Panel>
  </div>
);

const RevenueList = ({ items }) => {
  if (!items?.length) return <Empty />;
  const max = Math.max(...items.map((i) => i.revenue), 1);
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.name}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-white">{it.name}</span>
            <span className="text-zinc-400">{fmtEuro(it.revenue)} · {it.qty} u.</span>
          </div>
          <div className="h-1.5 bg-white/5">
            <div className="h-full bg-[color:var(--color-primary)]" style={{ width: `${(it.revenue / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const CustomersTab = ({ d }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <KPI label="Nouveaux acheteurs" value={fmtInt(d.new_buyers)} sub="Inscription + commande sur la période" testid="kpi-new-buyers" />
      <KPI label="Acheteurs récurrents" value={fmtInt(d.returning_buyers)} testid="kpi-returning" />
      <KPI label="Inscrits non-acheteurs" value={fmtInt(d.non_buyer_signups)} sub="Inscrits mais sans commande" testid="kpi-nonbuyers" />
      <KPI label="Commandes abandonnées" value={fmtInt(d.abandoned_pending_count)} sub="En attente > 48 h" icon={AlertCircle} testid="kpi-abandoned" />
    </div>

    <Panel title="Top 10 clients par revenu livré" testid="panel-top-customers">
      {d.top_customers?.length ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
              <th className="text-left py-2">Client</th>
              <th className="text-left py-2">Contact</th>
              <th className="text-right py-2">Commandes</th>
              <th className="text-right py-2">Total dépensé</th>
            </tr>
          </thead>
          <tbody>
            {d.top_customers.map((c) => (
              <tr key={c.user_id} className="border-b border-white/5">
                <td className="py-2 text-white">{c.name}</td>
                <td className="py-2 text-zinc-400 text-xs">{c.phone || c.email}</td>
                <td className="py-2 text-right text-white">{c.orders}</td>
                <td className="py-2 text-right text-[color:var(--color-primary)]">{fmtEuro(c.total_spent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty />}
    </Panel>

    <Panel title="Top 5 codes promo utilisés" testid="panel-promos">
      {d.promo_usage?.length ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
              <th className="text-left py-2">Code</th>
              <th className="text-right py-2">Utilisations</th>
              <th className="text-right py-2">Revenu</th>
              <th className="text-right py-2">Remise</th>
            </tr>
          </thead>
          <tbody>
            {d.promo_usage.map((p) => (
              <tr key={p.code} className="border-b border-white/5">
                <td className="py-2 text-white">
                  <Tag className="w-3 h-3 inline mr-1 text-[color:var(--color-primary)]" />{p.code}
                </td>
                <td className="py-2 text-right text-white">{p.uses}</td>
                <td className="py-2 text-right text-[color:var(--color-primary)]">{fmtEuro(p.revenue)}</td>
                <td className="py-2 text-right text-zinc-400">{fmtEuro(p.discount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty text="Aucun code promo utilisé sur la période" />}
    </Panel>
  </div>
);

const OperationsTab = ({ d }) => {
  const max = Math.max(...(d.heatmap || []).flat(), 1);
  const sv = d.stock_valuation || {};
  const byTier = sv.by_tier || {};
  const [tier, setTier] = useState('cost');
  // Backward-compat fallback if backend hasn't shipped by_tier yet
  const current = byTier[tier] || {
    label: "Prix d'achat",
    total_value: sv.total_value_cost,
    valued_units: sv.valued_units,
    valued_products: sv.valued_products,
    coverage_pct: sv.coverage_pct,
    top: sv.top_value_products || [],
  };
  const tierOptions = [
    { k: 'cost', label: "Prix d'achat", short: 'Achat' },
    { k: 'semi_gros', label: 'Prix semi-gros', short: 'Semi-gros' },
    { k: 'gros', label: 'Prix gros', short: 'Gros' },
  ];
  return (
    <div className="space-y-6">
      {/* Stock valuation block — multi-tier (Phase 33) */}
      <Panel title={`Valeur du stock au ${current.label.toLowerCase()}`} testid="panel-stock-valuation">
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="stock-tier-selector">
          <span className="text-zinc-500 text-[10px] uppercase tracking-widest mr-1">Base de valorisation :</span>
          {tierOptions.map((opt) => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setTier(opt.k)}
              data-testid={`stock-tier-${opt.k}`}
              className={`px-3 py-1.5 text-xs border transition-colors ${
                tier === opt.k
                  ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-black'
                  : 'border-white/10 text-zinc-400 hover:border-white/30'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <KPI
            label={`Valeur totale (${current.label.toLowerCase()})`}
            value={fmtEuro(current.total_value)}
            sub={`${current.valued_units || 0} / ${sv.total_units || 0} unités valorisées`}
            icon={Package}
            testid="kpi-stock-value"
          />
          <KPI
            label="Couverture"
            value={fmtPct(current.coverage_pct)}
            sub={`${current.valued_products || 0} produits valorisés`}
            testid="kpi-stock-coverage"
          />
          <KPI
            label={`Sans ${current.label.toLowerCase()}`}
            value={Math.max(0, (sv.total_units || 0) - (current.valued_units || 0))}
            sub="Unités à compléter"
            testid="kpi-stock-missing"
          />
          <KPI
            label="Total unités en stock"
            value={sv.total_units || 0}
            sub="Tous produits confondus"
            testid="kpi-stock-units"
          />
        </div>
        {current.coverage_pct != null && current.coverage_pct < 90 && (
          <div className="border border-orange-500/40 bg-orange-500/10 p-3 flex items-start gap-2 text-xs mb-4">
            <AlertCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
            <p className="text-orange-300">
              Couverture {fmtPct(current.coverage_pct)} pour « {current.label} » —
              certains produits n'ont pas ce prix renseigné. La valeur réelle est probablement supérieure à <span className="font-medium">{fmtEuro(current.total_value)}</span>.
              Complétez-les dans <span className="text-[color:var(--color-primary)]">Admin → Produits</span>.
            </p>
          </div>
        )}
        {current.top?.length > 0 && (
          <div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">
              Top 10 produits par valeur de stock — {current.label}
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="text-left py-2">Produit</th>
                  <th className="text-right py-2">Stock</th>
                  <th className="text-right py-2">{current.label}</th>
                  <th className="text-right py-2">Valeur</th>
                </tr>
              </thead>
              <tbody>
                {current.top.map((p) => (
                  <tr key={p.id}
                      className="border-b border-white/5"
                      data-testid={`stock-value-row-${p.id}`}>
                    <td className="py-2">
                      <p className="text-white text-sm">{p.name}</p>
                      <p className="text-zinc-500 text-xs">{p.brand}</p>
                    </td>
                    <td className="py-2 text-right text-zinc-300">{p.stock}</td>
                    <td className="py-2 text-right text-zinc-400">{fmtEuro(p.unit_price ?? p.cost_price)}</td>
                    <td className="py-2 text-right text-[color:var(--color-primary)] font-medium">{fmtEuro(p.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KPI
          label="Délai moyen de traitement"
          value={d.avg_processing_hours ? `${d.avg_processing_hours} h` : '—'}
          sub={d.delivered_sample_size ? `Sur ${d.delivered_sample_size} livraisons` : 'Pas assez de données'}
          icon={Check}
          testid="kpi-processing"
        />
        <KPI
          label="Appareils acheteurs"
          value={Object.keys(d.buyer_devices || {}).length || '—'}
          sub="Diversité d'accès"
          testid="kpi-buyer-devices"
        />
        <KPI
          label="Langues acheteurs"
          value={Object.keys(d.buyer_languages || {}).length || '—'}
          sub="Diversité linguistique"
          testid="kpi-buyer-langs"
        />
      </div>

      <Panel title="Heatmap des commandes (jour × heure)" testid="panel-heatmap">
        {d.heatmap?.length ? (
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="text-zinc-500 pr-2" />
                  {Array.from({ length: 24 }, (_, h) => (
                    <th key={`hr-head-${h}`} className="text-zinc-500 font-normal px-0.5 w-6 text-center">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.heatmap.map((row, wi) => (
                  <tr key={WEEKDAYS[wi]}>
                    <td className="text-zinc-500 pr-2 py-0.5">{WEEKDAYS[wi]}</td>
                    {row.map((v, h) => {
                      const alpha = v ? 0.15 + 0.85 * (v / max) : 0;
                      return (
                        <td
                          key={`${WEEKDAYS[wi]}-${h}`}
                          title={`${WEEKDAYS[wi]} ${h}h — ${v} commande${v > 1 ? 's' : ''}`}
                          className="w-6 h-6 text-center align-middle"
                          style={{
                            backgroundColor: v ? `rgba(212, 175, 55, ${alpha})` : 'rgba(255,255,255,0.03)',
                            color: alpha > 0.5 ? '#000' : '#999',
                            fontSize: '10px',
                          }}
                        >
                          {v || ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty />}
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="Appareils des acheteurs" testid="panel-buyer-devices">
          <DeviceBars items={d.buyer_devices} />
        </Panel>
        <Panel title="Langues des acheteurs" testid="panel-buyer-langs">
          <DeviceBars items={d.buyer_languages} />
        </Panel>
      </div>
    </div>
  );
};

// ========== Profits Tab ==========

const marginColor = (pct) => {
  if (pct >= 50) return 'text-emerald-400';
  if (pct >= 30) return 'text-[color:var(--color-primary)]';
  if (pct >= 10) return 'text-orange-400';
  return 'text-red-400';
};

const ProfitsTab = ({ d, scope, onScopeChange }) => {
  const t = d.totals || {};
  const coverage = t.coverage_pct ?? 100;
  const coverageLow = coverage < 80;
  return (
    <div className="space-y-6" data-testid="profits-tab">
      {/* Scope selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-zinc-500 text-xs uppercase tracking-[0.2em]">Périmètre&nbsp;:</span>
        {[
          { id: 'realized', label: 'Réalisé (expédié + livré)' },
          { id: 'potential', label: 'Potentiel (toutes commandes en cours)' },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onScopeChange(opt.id)}
            className={`px-3 py-1.5 text-xs border transition-colors ${
              scope === opt.id
                ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-black'
                : 'border-white/10 text-zinc-400 hover:border-white/30'
            }`}
            data-testid={`profit-scope-${opt.id}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Coverage alert */}
      {coverageLow && (
        <div
          className="border border-orange-500/40 bg-orange-500/10 p-4 flex items-start gap-3"
          data-testid="profit-coverage-alert"
        >
          <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-orange-300 font-medium">
              Couverture des prix d'achat&nbsp;: {fmtPct(coverage)}
            </p>
            <p className="text-zinc-400 text-xs mt-1">
              {t.missing_lines} ligne{t.missing_lines > 1 ? 's' : ''} sans prix d'achat — le bénéfice affiché surestime la marge réelle. Complétez les prix dans <span className="text-[color:var(--color-primary)]">Admin → Produits → Importer prix d'achat</span>.
            </p>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Chiffre d'affaires" value={fmtEuro(t.revenue)} icon={TrendingUp} testid="kpi-profit-revenue" />
        <KPI label="Coût total" value={fmtEuro(t.cost)} icon={Package} testid="kpi-profit-cost" />
        <KPI label="Bénéfice" value={fmtEuro(t.profit)} sub={`${t.orders || 0} commandes`} icon={DollarSign} testid="kpi-profit-profit" />
        <KPI label="Marge globale" value={fmtPct(t.margin_pct)} sub={`Couverture coût : ${fmtPct(coverage)}`} icon={Tag} testid="kpi-profit-margin" />
      </div>

      {/* Daily chart */}
      <Panel title="Bénéfice par jour (€)" testid="chart-daily-profit">
        {d.daily?.some((r) => r.revenue || r.cost) ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.daily}>
              <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={shortDate} stroke="var(--color-text-muted)" tick={{ fontSize: 11 }} />
              <YAxis stroke="var(--color-text-muted)" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: BG, border: '1px solid var(--color-border)' }}
                labelFormatter={shortDate}
                formatter={(v, n) => [fmtEuro(v), n]}
              />
              <Line type="monotone" dataKey="revenue" stroke="#7dd3fc" strokeWidth={2} dot={false} name="Revenu" />
              <Line type="monotone" dataKey="cost" stroke="#f87171" strokeWidth={2} dot={false} name="Coût" />
              <Line type="monotone" dataKey="profit" stroke={GOLD} strokeWidth={2} dot={false} name="Bénéfice" />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Top 10 clients par bénéfice" testid="panel-top-customers-profit">
          {d.top_customers?.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="text-left py-2">Client</th>
                  <th className="text-right py-2">Bénéfice</th>
                  <th className="text-right py-2">Marge</th>
                </tr>
              </thead>
              <tbody>
                {d.top_customers.map((c) => (
                  <tr
                    key={c.user_id}
                    className="border-b border-white/5"
                    data-testid={`profit-customer-${c.user_id}`}
                  >
                    <td className="py-2">
                      <p className="text-white text-sm">
                        {c.name}
                        {c.has_missing_cost && (
                          <span
                            className="ml-2 text-[10px] text-orange-400 uppercase"
                            title="Une partie des produits n'a pas de prix d'achat"
                          >
                            ⚠
                          </span>
                        )}
                      </p>
                      <p className="text-zinc-500 text-xs">
                        {c.orders} cmd · {fmtEuro(c.revenue)}
                      </p>
                    </td>
                    <td className="py-2 text-right text-white font-medium">{fmtEuro(c.profit)}</td>
                    <td className={`py-2 text-right text-sm ${marginColor(c.margin_pct)}`}>
                      {fmtPct(c.margin_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty />}
        </Panel>

        <Panel title="Top 10 produits par bénéfice" testid="panel-top-products-profit">
          {d.top_products?.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="text-left py-2">Produit</th>
                  <th className="text-right py-2">Bénéfice</th>
                  <th className="text-right py-2">Marge</th>
                </tr>
              </thead>
              <tbody>
                {d.top_products.map((p) => (
                  <tr
                    key={p.product_id}
                    className="border-b border-white/5"
                    data-testid={`profit-product-${p.product_id}`}
                  >
                    <td className="py-2">
                      <p className="text-white text-sm">
                        {p.name}
                        {!p.has_cost && (
                          <span
                            className="ml-2 text-[10px] text-orange-400 uppercase"
                            title="Prix d'achat manquant"
                          >
                            ⚠
                          </span>
                        )}
                      </p>
                      <p className="text-zinc-500 text-xs">{p.qty} u · {fmtEuro(p.revenue)}</p>
                    </td>
                    <td className="py-2 text-right text-white font-medium">{fmtEuro(p.profit)}</td>
                    <td className={`py-2 text-right text-sm ${marginColor(p.margin_pct)}`}>
                      {p.has_cost ? fmtPct(p.margin_pct) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty />}
        </Panel>
      </div>

      <Panel title="Marges les plus faibles — produits à revoir" testid="panel-worst-margin-products">
        {d.worst_margin_products?.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="text-left py-2">Produit</th>
                <th className="text-right py-2">Qté</th>
                <th className="text-right py-2">Revenu</th>
                <th className="text-right py-2">Coût</th>
                <th className="text-right py-2">Marge</th>
              </tr>
            </thead>
            <tbody>
              {d.worst_margin_products.map((p) => (
                <tr
                  key={`worst-${p.product_id}`}
                  className="border-b border-white/5"
                  data-testid={`profit-worst-${p.product_id}`}
                >
                  <td className="py-2 text-white">{p.name}</td>
                  <td className="py-2 text-right text-zinc-400">{p.qty}</td>
                  <td className="py-2 text-right text-zinc-400">{fmtEuro(p.revenue)}</td>
                  <td className="py-2 text-right text-zinc-400">{fmtEuro(p.cost)}</td>
                  <td className={`py-2 text-right ${marginColor(p.margin_pct)}`}>
                    {fmtPct(p.margin_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <Empty text="Aucun produit avec prix d'achat connu sur cette période" />}
      </Panel>
    </div>
  );
};

export default AdminAnalytics;
